// Theme store — light / dark / auto preference with OS-level listener.

import { create } from 'zustand'

export type ThemePreference = 'light' | 'dark' | 'auto'
export type EffectiveTheme = 'light' | 'dark'

const THEME_KEY = 'tmc-theme-preference'
const isBrowser = typeof window !== 'undefined'

function getSystemPreference(): EffectiveTheme {
  if (!isBrowser) return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function getEffectiveTheme(preference: ThemePreference): EffectiveTheme {
  if (preference === 'auto') return getSystemPreference()
  return preference
}

function applyTheme(effective: EffectiveTheme): void {
  if (!isBrowser) return
  document.documentElement.setAttribute('data-theme', effective)
}

function getStoredPreference(): ThemePreference {
  if (!isBrowser) return 'auto'
  try {
    return (localStorage.getItem(THEME_KEY) as ThemePreference) || 'auto'
  } catch {
    return 'auto'
  }
}

interface ThemeState {
  preference: ThemePreference
  effective: EffectiveTheme
  setPreference: (preference: ThemePreference) => void
  _onSystemChange: () => void
}

const storedPref = getStoredPreference()

export const useThemeStore = create<ThemeState>((set, get) => ({
  preference: storedPref,
  effective: getEffectiveTheme(storedPref),

  setPreference: (preference) => {
    const effective = getEffectiveTheme(preference)
    try { localStorage.setItem(THEME_KEY, preference) } catch { /* noop */ }
    applyTheme(effective)
    set({ preference, effective })
  },

  _onSystemChange: () => {
    const { preference } = get()
    if (preference === 'auto') {
      const effective = getSystemPreference()
      applyTheme(effective)
      set({ effective })
    }
  },
}))

// Apply initial theme + listen for OS changes (browser only)
if (isBrowser) {
  applyTheme(useThemeStore.getState().effective)
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    useThemeStore.getState()._onSystemChange()
  })
}
